// ./src/components/features/Connectors/ConnectorEditModal.tsx
import React, { useState, useEffect } from "react";
import { Stack, TextInput, Textarea, Accordion, Text } from "@mantine/core";
import { Connector, ConnectorInput } from "@/utils/api";
import { AppDrawer, StatusSelector } from "@/components/generic";
import { ConnectorCategorySelector } from "../ConnectorCategorySelector";
import { ConnectorTypeSelector } from "../ConnectorTypeSelector";
import { ConnectorConfigEditor } from "../ConnectorConfigEditor";

export interface ConnectorEditModalProps {
  /** Whether the modal is open */
  opened: boolean;

  /** Function to close the modal */
  onClose: () => void;

  /** Connector to edit (if null/undefined, it's create mode) */
  connector?: Connector | null;

  /** Callback when connector should be created */
  onCreateConnector?: (data: ConnectorInput) => Promise<Connector | null>;

  /** Callback when connector should be updated */
  onUpdateConnector?: (
    id: string,
    data: ConnectorInput
  ) => Promise<Connector | null>;

  /** Whether create operation is in progress */
  isCreating?: boolean;

  /** Whether update operation is in progress */
  isUpdating?: boolean;

  /** Last error from operations */
  lastError?: string | null;

  /** Callback to clear errors */
  onClearError?: () => void;
}

interface FormData {
  name: string;
  description: string;
  connectorCategory: string | null;
  connectorType: string | null;
  connectorConfiguration: string;
  status: number;
}

const initialFormData: FormData = {
  name: "",
  description: "",
  connectorCategory: null,
  connectorType: null,
  connectorConfiguration: "{}",
  status: 100,
};

export const ConnectorEditModal: React.FC<ConnectorEditModalProps> = ({
  opened,
  onClose,
  connector,
  onCreateConnector,
  onUpdateConnector,
  isCreating = false,
  isUpdating = false,
  lastError,
  onClearError,
}) => {
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string | null | undefined>
  >({});

  const isEditMode = Boolean(connector?.id);
  const isSubmitting = isCreating || isUpdating;

  // Reset form when modal opens/closes or connector changes
  useEffect(() => {
    if (opened) {
      if (connector) {
        // Edit mode - populate form with data
        setFormData({
          name: connector.name || "",
          description: connector.description || "",
          connectorCategory: connector.connectorCategory || null,
          connectorType: connector.connectorType || null,
          connectorConfiguration: connector.connectorConfiguration || "{}",
          status: connector.status ?? 100,
        });
      } else {
        // Create mode - use defaults
        setFormData(initialFormData);
      }

      setValidationErrors({});
      onClearError?.();
    }
  }, [opened, connector, onClearError]);

  // Validate form
  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    // Name is required
    if (!formData.name.trim()) {
      errors.name = "Connector name is required";
    }

    // Category is required
    if (!formData.connectorCategory) {
      errors.connectorCategory = "Category is required";
    }

    // Type is required
    if (!formData.connectorType) {
      errors.connectorType = "Type is required";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle field changes
  const handleFieldChange = (field: keyof FormData, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear validation error when user starts typing
    if (validationErrors[field]) {
      setValidationErrors((prev) => ({
        ...prev,
        [field]: undefined,
      }));
    }
  };

  // Handle category change - also reset type and configuration
  const handleCategoryChange = (category: string | null) => {
    setFormData((prev) => ({
      ...prev,
      connectorCategory: category,
      connectorType: null,
      connectorConfiguration: "{}",
    }));

    // Clear related validation errors
    setValidationErrors((prev) => ({
      ...prev,
      connectorCategory: undefined,
      connectorType: undefined,
    }));
  };

  // Handle type change - reset configuration
  const handleTypeChange = (type: string | null) => {
    setFormData((prev) => ({
      ...prev,
      connectorType: type,
      connectorConfiguration: "{}",
    }));

    // Clear validation error
    if (validationErrors.connectorType) {
      setValidationErrors((prev) => ({
        ...prev,
        connectorType: undefined,
      }));
    }
  };

  // Handle save
  const handleSubmit = async () => {
    onClearError?.();

    if (!validateForm()) {
      return;
    }

    try {
      const submitData: ConnectorInput = {
        name: formData.name.trim(),
        description: formData.description.trim() || null,
        connectorCategory: formData.connectorCategory,
        connectorType: formData.connectorType,
        connectorConfiguration: formData.connectorConfiguration || "{}",
        status: formData.status,
      };

      let result: Connector | null = null;

      if (isEditMode && connector?.id && onUpdateConnector) {
        result = await onUpdateConnector(connector.id, submitData);
      } else if (!isEditMode && onCreateConnector) {
        result = await onCreateConnector(submitData);
      }

      if (result) {
        onClose();
      }
    } catch (error) {
      console.error("Connector submission error:", error);
    }
  };

  return (
    <AppDrawer
      opened={opened}
      onClose={onClose}
      title={isEditMode ? "Edit Connector" : "Create Connector"}
      position="right"
      size="lg"
      closable={!isSubmitting}
      loading={isSubmitting}
      errors={lastError}
      actionType="save-cancel"
      onConfirm={handleSubmit}
    >
      <Stack gap="md">
        {/* Basic Information */}
        <TextInput
          label="Connector Name"
          placeholder="Enter connector name"
          value={formData.name}
          onChange={(e) => handleFieldChange("name", e.target.value)}
          error={validationErrors.name}
          required
          withAsterisk
          size="md"
        />

        <Textarea
          label="Description"
          placeholder="Enter description (optional)"
          value={formData.description}
          onChange={(e) => handleFieldChange("description", e.target.value)}
          description="A note to help identify this connector's purpose"
          minRows={2}
          maxRows={4}
          autosize
          size="md"
        />

        <ConnectorCategorySelector
          label="Category"
          value={formData.connectorCategory}
          onChange={handleCategoryChange}
          error={validationErrors.connectorCategory}
          required
          size="md"
        />

        <ConnectorTypeSelector
          label="Type"
          value={formData.connectorType}
          onChange={handleTypeChange}
          category={formData.connectorCategory}
          error={validationErrors.connectorType}
          required
          size="md"
        />

        <StatusSelector
          label="Status"
          value={formData.status}
          onChange={(value) => handleFieldChange("status", value)}
          size="md"
        />

        {/* Configuration Section */}
        <Accordion variant="separated" radius="md" defaultValue="configuration">
          <Accordion.Item value="configuration">
            <Accordion.Control>
              <Text fw={500}>Configuration</Text>
            </Accordion.Control>
            <Accordion.Panel>
              <ConnectorConfigEditor
                connectorType={formData.connectorType}
                value={formData.connectorConfiguration}
                onChange={(value) =>
                  handleFieldChange("connectorConfiguration", value)
                }
                disabled={isSubmitting}
              />
            </Accordion.Panel>
          </Accordion.Item>
        </Accordion>
      </Stack>
    </AppDrawer>
  );
};
